import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { IndianRupee, Loader2, Receipt, Search, Wallet } from 'lucide-react';
import { billingApi, type PatientDueBalanceRow, type PatientDueBillRow } from '@/api/billing';
import ViewBill from '@/components/billing/ViewBill';
import { ReportHospitalFilter } from '@/components/reports/ReportHospitalFilter';
import { useChainReportHospitalScope } from '@/hooks/useChainReportHospitalScope';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function formatCurrency(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PatientDueBalancesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: 'consolidated' });

  const scopeHospitalId = useMemo(() => {
    if (!showHospitalFilter) return undefined;
    if (!reportHospitalId && isSuperAdmin) return undefined;
    return reportHospitalId ?? 'all';
  }, [showHospitalFilter, reportHospitalId, isSuperAdmin]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    () => searchParams.get('patientId')
  );
  const [viewBillId, setViewBillId] = useState<string | null>(null);
  const [viewBillOpen, setViewBillOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const listEnabled = !showHospitalFilter || !!scopeHospitalId || !isSuperAdmin;

  const { data: listRows = [], isLoading: listLoading, isFetching: listFetching } = useQuery({
    queryKey: ['patient-due-balances', debouncedSearch, scopeHospitalId ?? 'single'],
    queryFn: () =>
      billingApi.listPatientDueBalances(debouncedSearch || undefined, scopeHospitalId),
    enabled: listEnabled,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['patient-due-balance', selectedPatientId, scopeHospitalId ?? 'single'],
    queryFn: () => billingApi.getPatientDueBalance(selectedPatientId!, scopeHospitalId),
    enabled: !!selectedPatientId && listEnabled,
  });

  const totalOutstanding = useMemo(
    () => listRows.reduce((s, r) => s + r.totalDue, 0),
    [listRows]
  );

  useEffect(() => {
    const fromUrl = searchParams.get('patientId');
    if (fromUrl && fromUrl !== selectedPatientId) {
      setSelectedPatientId(fromUrl);
    }
  }, [searchParams, selectedPatientId]);

  const selectPatient = (row: PatientDueBalanceRow) => {
    setSelectedPatientId(row.patientId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('patientId', row.patientId);
      return next;
    });
  };

  const openCollect = (bill: PatientDueBillRow) => {
    setViewBillId(bill.billId);
    setViewBillOpen(true);
  };

  const onPaymentSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['patient-due-balances'] });
    queryClient.invalidateQueries({ queryKey: ['patient-due-balance'] });
    queryClient.invalidateQueries({ queryKey: ['patient-due-summary'] });
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="h-7 w-7 text-amber-600" />
            Package due amounts
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Outstanding balances on Additional Services package bills (EMI). Collect the full remaining
            amount or the next installment from the bill screen.
          </p>
        </div>
        {showHospitalFilter ? (
          <ReportHospitalFilter
            hospitals={hospitalOptions}
            value={selectedHospitalId}
            onChange={setSelectedHospitalId}
            isSuperAdmin={isSuperAdmin}
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Patients with due</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">{listRows.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total outstanding</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-amber-800">{formatCurrency(totalOutstanding)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Patients</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by name, phone, or UHID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            {listLoading || listFetching ? (
              <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading…
              </div>
            ) : listRows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No patients with package dues in this scope.</p>
            ) : (
              <div className="rounded-md border overflow-auto max-h-[520px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="text-right">Due</TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listRows.map((row) => (
                      <TableRow
                        key={`${row.patientId}-${row.hospitalId}`}
                        className={selectedPatientId === row.patientId ? 'bg-amber-50' : undefined}
                      >
                        <TableCell>
                          <div className="font-medium">{row.patientName}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.phone ?? '—'}
                            {row.uhid ? ` · ${row.uhid}` : ''}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{row.hospitalName ?? '—'}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-amber-900">
                          {formatCurrency(row.totalDue)}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => selectPatient(row)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-amber-600" />
              Open bills
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedPatientId ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Select a patient to see open package bills and collect payment.
              </p>
            ) : detailLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading bills…
              </div>
            ) : !detail?.bills?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No open dues for this patient (may have been cleared).
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold">{detail.patient.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Total due:{' '}
                      <span className="font-medium text-amber-900">{formatCurrency(detail.totalDue)}</span>
                    </p>
                  </div>
                  <Badge variant="outline">{detail.bills.length} bill(s)</Badge>
                </div>
                <div className="rounded-md border overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bill</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead className="text-right">Package</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Due</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.bills.map((bill) => (
                        <TableRow key={bill.billId}>
                          <TableCell className="whitespace-nowrap">
                            <div className="font-mono text-xs">{bill.billNumber}</div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(bill.billDate), 'dd MMM yyyy')}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm max-w-[180px] truncate" title={bill.serviceSummary}>
                            {bill.serviceSummary}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(bill.totalAmount)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(bill.paidAmount)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-amber-900">
                            {formatCurrency(bill.dueAmount)}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" onClick={() => openCollect(bill)}>
                              <Receipt className="h-4 w-4 mr-1" />
                              Collect
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Use <strong>Collect</strong> to open the bill and record full or partial payment (next EMI).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ViewBill
        billId={viewBillId}
        isOpen={viewBillOpen}
        onClose={() => {
          setViewBillOpen(false);
          setViewBillId(null);
        }}
        onSuccess={onPaymentSuccess}
      />
    </div>
  );
}
